import { addMinutes } from 'date-fns';
import { AcceptRequestFlowValues } from '../../types/appointments';

const getPriceFields = (values: AcceptRequestFlowValues) => {
  if (values.requestKind === 'CONSULTATION') {
    return {
      priceType: 'FIXED',
      price: 0,
      depositPrice: 0,
      minPrice: 0,
      maxPrice: 0,
      hourlyPrice: 0,
    };
  }
  return {
    priceType: values.priceType,
    depositPrice: values.depositPrice || 0,
    ...(values.priceType === 'FIXED' && { price: values.price || 0 }),
    ...(values.priceType === 'HOURLY' && {
      hourlyPrice: values.hourlyPrice || 0,
    }),
    ...(values.priceType === 'RANGE' && {
      minPrice: values.minPrice || 0,
      maxPrice: values.maxPrice || 0,
    }),
  };
};

export function buildAcceptRequestBody(values: AcceptRequestFlowValues) {
  const isConsultation = values.requestKind === 'CONSULTATION';
  const isOfferTimes = values.schedulingMode === 'OFFER_TIMES';
  // Offered times are still an acceptance — the client picks from the link
  const isBookingLink =
    values.schedulingMode === 'BOOKING_LINK' || isOfferTimes;
  const lengthInt = parseInt(values.appointmentLengthInMinutes, 10);
  const isMultiSession = !isConsultation && values.numberOfSessions > 1;

  const body: Record<string, unknown> = {
    status: isBookingLink ? 'ACCEPTED' : 'SCHEDULED',
    // The server only applies consultation handling (zeroed price/deposit,
    // virtual location) when the payload names the type explicitly
    appointmentType: isConsultation ? 'CONSULTATION' : 'NEW_APPOINTMENT',
    employeeId: values.employeeId,
    appointmentLengthInMinutes: lengthInt,
    customDesignMessage: values.customDesignMessage,
    cashDiscountEnabled: values.cashDiscountEnabled,
    tattooLocation: values.tattooLocation,
    bodySide: values.bodySide,
    tattooSize: values.tattooSize,
    tattooColor: values.tattooColor,
    tattooDetails: values.tattooDetails,
    ...(values.serviceId &&
      values.serviceId !== '0' && {
        piercingLocationId: values.piercingLocationId
          ? parseInt(values.piercingLocationId, 10)
          : null,
      }),
    ...(values.internalNotes && { internalNotes: values.internalNotes }),
    ...getPriceFields(values),
    // Only an actual choice is sent — the server persists an explicit "No"
    // as a waiver opt-out, so a hidden field must not send anything.
    // Consultations send nothing: the server already never requests their waiver
    ...(!isConsultation &&
      values.requestWaiver !== undefined && {
        requestWaiver: values.requestWaiver,
      }),
  };

  if (isConsultation) {
    body.isVirtual = !!values.isVirtual;
    body.allowOverlapping = !!values.allowOverlapping;
    if (values.isVirtual) {
      body.virtualMeetingUrl = values.virtualMeetingUrl || undefined;
      body.virtualMeetingProvider = values.virtualMeetingProvider || undefined;
    }
  }

  if (isBookingLink) {
    if (isMultiSession) {
      body.accept = {
        numberOfSessions: values.numberOfSessions,
        schedulingType: values.schedulingType || undefined,
        bufferDays:
          values.bufferDays === '' || values.bufferDays === undefined
            ? undefined
            : Number(values.bufferDays),
        depositPlacement: values.depositPlacement || undefined,
      };
    } else {
      body.accept = {
        numberOfSessions: 1,
        ...(isOfferTimes && { offeredSlots: values.offeredSlots }),
      };
    }
    return body;
  }

  const startDate = new Date(values.startDateTime!);
  body.startDateTime = startDate.toISOString();
  body.endDateTime = addMinutes(startDate, lengthInt).toISOString();

  if (isMultiSession) {
    body.additionalAppointments = values.additionalAppointments.map(session => {
      const sessionLength = session.appointmentLengthInMinutes
        ? parseInt(session.appointmentLengthInMinutes, 10)
        : lengthInt;
      const sessionStart = new Date(session.startDateTime);
      return {
        startDateTime: sessionStart.toISOString(),
        endDateTime: addMinutes(sessionStart, sessionLength).toISOString(),
        appointmentLengthInMinutes: sessionLength,
      };
    });
    if ((values.depositPrice || 0) > 0) {
      body.depositPlacement = values.depositPlacement || undefined;
    }
  }

  return body;
}
